import { Box, Button, Container, Flex, FormControl, Heading, Image, Text, useToast } from "@chakra-ui/react";

import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { getEffectiveUserRole } from "utils/roles";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Store } from "store";

// third party
import AppInput from "components/AppInput";
import { useFormik } from "formik";
import { showToast, toastTypes } from "utils";
import * as Yup from "yup";

export default function ChangePasswordFirstLogin() {
  const toast = useToast();
  const { dispatch } = useContext(Store);
  const navigate = useNavigate();
  const { changePasswordFirstLogin } = useApi();
  const { user } = useAuthentication();
  
  const initialValues = {
    password: "",
    passwordConfirmation: "",
    submit: null,
  };
  
  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .min(6, "Le mot de passe doit contenir au moins 6 caractères")
      .required("Le mot de passe est requis"),
    passwordConfirmation: Yup.string()
      .required("La confirmation du mot de passe est requise")
      .oneOf([Yup.ref("password")], "Les mots de passe doivent être identiques"),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async ({ password, passwordConfirmation }) => {
      try {
        const response: any = await changePasswordFirstLogin(password, passwordConfirmation);
        if (response?.ok || !response?.error_code) {
          dispatch(
            showToast({
              toast: toast,
              message: "Mot de passe changé avec succès",
              status: toastTypes.success,
              description: "Vous pouvez maintenant accéder à votre tableau de bord"
            })
          );
          
          // Déterminer la destination selon le rôle (structure API : role à la racine ou dans user)
          const userRole = getEffectiveUserRole(user);
          const taxDashboardRoles = [
            "taxsuperadmin",
            "taxadmin", 
            "taxcollector",
            "taxsupervisor",
            "dealer",
            "taxpayer",
            "debitor"
          ];
          
          // Rediriger selon le rôle
          if (userRole && taxDashboardRoles.includes(userRole)) {
            navigate("/tax/dashboard");
          } else {
            navigate("/dashboard");
          }
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.message || "Impossible de changer le mot de passe",
              status: toastTypes.error,
            })
          );
          setFieldValue("password", "");
          setFieldValue("passwordConfirmation", "");
        }
      } catch (ex) {
        dispatch(
          showToast({
            toast: toast,
            message: "Une erreur est survenue lors du changement de mot de passe",
            status: toastTypes.error,
          })
        );
        setFieldValue("password", "");
        setFieldValue("passwordConfirmation", "");
      }
    },
  });
  
  return (
    <>
      {/* welcome section */}
      <Flex flexDirection="column" alignItems="center" mt={10}>
        <Container
          gap={{ md: "20px", base: "88px", sm: "132px" }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          px={{ md: "56px", base: "20px" }}
          p={{ md: 0, base: "20px" }}
        >
          <Flex
            w={{ md: "86%", base: "100%" }}
            justifyContent="center"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Box
              w={{ md: "46%", base: "100%" }}
              display={{ md: "flex", base: "none" }}
            >
              <Flex
                borderTopLeftRadius="24px"
                borderBottomLeftRadius="24px"
                gap="20px"
                bg="red_origin"
                flexDirection="column"
                px={{ base: "20px", sm: "32px" }}
              >
                <Flex
                   mt="20px"
                   gap="16px"
                   flexDirection="column"
                   alignItems="start"
                >
                  <Text size="text3xl" color="white" textAlign={"center"}>
                    Bienvenue sur ORIGIN360
                  </Text>
                  <Text
                    size="textxl"
                    color="very_light_gray"
                    w="100%"
                    lineHeight="150%"
                    textAlign={"center"}
                  >
                    Certifiez, gérez, encaissez en toute simplicité.
                  </Text>
                </Flex>
                <Box bg={"white"} h="200px"
                  w="200px" borderRadius={"25px"} mx={"auto"}>
                  <Image
                    src="images/qr-code-login-1.png"
                    alt="Decorative Image"
                    h="200px"
                    w="200px"
                    fit="contain"
                    mx={"auto"}
                  />
                </Box>
              </Flex>
            </Box>
            <Flex
              flex={1}
              flexDirection="column"
              alignItems="start"
              alignSelf={{ md: "auto", base: "stretch" }}
            >
              <form
                onSubmit={handleSubmit}
                style={{ flex: 1, alignSelf: "stretch" }}
              >
                <Flex
                  borderTopRightRadius="24px"
                  borderBottomRightRadius="24px"
                  gap="42px"
                  bg="white"
                  w={{ md: "88%", base: "100%" }}
                  flexDirection="column"
                  alignItems="center"
                  p={{ base: "20px", sm: "22px" }}
                  height={"100%"}
                >
                  <Flex
                    gap="2px"
                    w={{ md: "90%", base: "100%" }}
                    flexDirection="column"
                  >
                    <Image
                      src="images/logo.png"
                      alt="Login Image"
                      h="112px"
                      w="112px"
                      fit="contain"
                      mx={"auto"}
                    />
                    <Flex flexDirection="column" alignItems="start">
                      <Heading size="headingxl" as="h1">
                        Changer votre mot de passe
                      </Heading>
                      <Text size="textxl" color="gray_description">
                        Pour des raisons de sécurité, vous devez changer votre mot de passe pour continuer.
                      </Text>
                    </Flex>
                  </Flex>
                  <Flex
                    gap="8px"
                    w={{ md: "90%", base: "100%" }}
                    flexDirection="column"
                  >
                    <Flex gap="12px" flexDirection="column">
                      <FormControl
                        isInvalid={!!errors.password && touched.password}
                      >
                        <AppInput
                          required
                          placeholder="Nouveau mot de passe"
                          type="password"
                          fontFamily="Arimo"
                          gap="8px"
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          name="password"
                          autoComplete="new-password"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.password || ""}
                          showErrorMessage={Boolean(
                            touched.password && errors.password
                          )}
                          errorMessage={errors.password}
                        />
                      </FormControl>
                      <FormControl
                        isInvalid={!!errors.passwordConfirmation && touched.passwordConfirmation}
                      >
                        <AppInput
                          required
                          placeholder="Confirmer le mot de passe"
                          gap="8px"
                          type="password"
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="passwordConfirmation"
                          name="passwordConfirmation"
                          autoComplete="new-password"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.passwordConfirmation || ""}
                          showErrorMessage={Boolean(
                            touched.passwordConfirmation && errors.passwordConfirmation
                          )}
                          errorMessage={errors.passwordConfirmation}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                  <Flex
                    mb="46px"
                    gap="12px"
                    w={{ md: "34%", base: "100%" }}
                    flexDirection="column"
                  >
                    <Button
                      size="4xl"
                      colorScheme="red_origin"
                      fontWeight={700}
                      boxShadow="md"
                      alignSelf="stretch"
                      borderRadius="8px"
                      type="submit"
                      fontSize={"15px"}
                      width={"180px"}
                      isLoading={isSubmitting}
                    >
                      Changer le mot de passe
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </Flex>
          </Flex>
          <Text
            size="texts"
            color="gray_description"
            fontFamily="Arimo"
            fontWeight={500}
          >
            Origin360. Tous droits réservés © {new Date().getFullYear()}
          </Text>
        </Container>
      </Flex>
    </>
  );
}

